/**
 * Entity Icon Configuration
 *
 * Centralized configuration for entity type icons and display names.
 *
 * VISUAL: every entity now uses the same neutral grey tile
 * (`bg.subtle` background + `text.primary` icon). Was previously a per-type
 * tinted system (sage / copper / brown), which conflicted with the new
 * monochrome aesthetic. Type differentiation is carried by the icon SHAPE
 * alone — the surrounding tile stays consistent across notes, collections,
 * attachments, and protocols.
 *
 * Display names: user-facing labels for entity types. Internal code
 * (variables, API routes, database) still uses "project"; only the UI and
 * agent-facing text uses "collection".
 *
 * Source of truth: `displayName` and `materialIcon` for artifact subtypes
 * (note, attachment, protocol, equipment) are derived from
 * `@bower/shared`'s `ARTIFACT_TYPE_CONFIG` to avoid drift. Frontend-only
 * fields (`glyph` GlyphRef, `pluralName`, `color`, `bgColor`,
 * `searchable`) and non-artifact entity types (`workspace`, `project`,
 * `subproject`) remain local.
 */

import { ARTIFACT_TYPE_CONFIG } from '@bower/shared/constants/artifact-types.generated';
import type { FileFacts } from '@bower/shared';
import { fileGlyphRef, getFileGlyph } from '../utils/getFileGlyph';
import { bowerGlyph, materialGlyph, type GlyphRef } from './glyph';

export type EntityType =
  | 'workspace'
  | 'project'
  | 'note'
  | 'attachment'
  | 'protocol'
  | 'subproject'
  | 'equipment'
  | 'spreadsheet'
  | 'link';

export interface EntityIconConfig {
  /**
   * The glyph, and the library that holds it — see `theme/glyph.ts`.
   *
   * ONE field, not `icon` + an optional `materialIcon` override. The override
   * shape meant every consumer re-derived the library (`materialIcon ?? icon`
   * paired with `materialIcon ? 'material' : 'bower'`), and a consumer that
   * read only `icon` silently rendered the wrong glyph for `equipment` — the
   * one entry that uses the other library.
   */
  glyph: GlyphRef;
  color: string; // Semantic token for accent color (text, badges)
  bgColor: string; // Semantic token for background color (auto light/dark)
  displayName: string; // User-facing singular name (e.g. "Collection")
  pluralName: string; // User-facing plural name (e.g. "Collections")
  /** When true, this entity type appears as a filter chip in global search and artifact list pages. */
  searchable?: boolean;
}

// Single neutral tile — same colour for every entity type. Differentiation
// is carried by the icon SHAPE, never by the surround.
//
// 2026-09: that principle is unchanged, but the icons themselves are now
// painted in the brand gradient (`theme/iconGradient.ts`) and are usually
// rendered with no tile at all. The gradient is IDENTICAL for every entity
// type, so it is brand presence rather than type encoding and the
// shape-carries-meaning rule above still holds — a note and a collection are
// told apart by their glyph, exactly as before.
//
// `bgColor` is now TRANSPARENT, and that is the whole of the "no grey chip"
// change. Six surfaces render this tile — EntityIconTile (itself shared by
// search results, Move/Copy destinations and EntityTypeBadge), BaseCard,
// CaptureRow and the WorkspaceArtifacts type column — and they read it from
// here, so pointing it at a color is how all six move at once. They keep the
// BOX: it holds the fixed dimensions that align every row, and only its fill
// goes away.
//
// Do NOT "fix" this to a surface color. A white chip is not neutral on a white
// panel, it is an invisible chip plus a misaligned glyph, and it silently
// diverges from the six surfaces' own backgrounds. Transparent is the only
// value that is correct on all of them.
//
// `color` is the inherited ink a gradient glyph falls back to WHERE A CALLER
// PUTS IT ON THE WRAPPER (EntityIconTile, CaptureRow, the WorkspaceArtifacts
// type column) and the SVG paint server is missing. Keep it legible.
//
// It is NOT the print fallback — `iconGradient.ts` hardcodes that to the mid
// stop — and forced-colors overrides it with a system color. Editing this
// value moves neither of those branches, so do not expect it to.
const NEUTRAL_TILE = {
  color: 'text.primary',
  bgColor: 'transparent',
} as const;

/**
 * Entity icon configuration
 * Maps entity types to their visual representation and display names
 */
export const ENTITY_ICONS: Record<EntityType, EntityIconConfig> = {
  // ── Non-artifact entity types — kept local (not covered by the shared registry) ──

  workspace: {
    glyph: bowerGlyph('workspace'),
    ...NEUTRAL_TILE,
    displayName: 'Workspace',
    pluralName: 'Workspaces',
  },
  project: {
    glyph: bowerGlyph('collection'),
    ...NEUTRAL_TILE,
    displayName: 'Collection',
    pluralName: 'Collections',
    searchable: true,
  },
  // A collection nested inside another. Its label must differ from
  // `project`: EntityTypeBadge derives both the tooltip and the aria-label
  // from displayName, so sharing "Collection" made a sub-collection row
  // indistinguishable from a top-level one on hover and to screen readers.
  subproject: {
    glyph: bowerGlyph('sub-collection'),
    ...NEUTRAL_TILE,
    displayName: 'Sub-collection',
    pluralName: 'Sub-collections',
  },

  // ── Artifact subtypes — displayName derived from @bower/shared ──
  // The GLYPH stays local: the shared registry names Material Symbols, and
  // most of these are drawn in Bower's own set. `equipment` is the exception
  // and takes the shared Material name, which is exactly the case the old
  // `icon` + `materialIcon` override existed to express and now needs no
  // special shape. `pluralName` is frontend-only.

  note: {
    glyph: bowerGlyph('note'),
    ...NEUTRAL_TILE,
    displayName: ARTIFACT_TYPE_CONFIG.note.displayName,
    pluralName: 'Notes',
    searchable: true,
  },
  attachment: {
    glyph: bowerGlyph('attachment'),
    ...NEUTRAL_TILE,
    displayName: ARTIFACT_TYPE_CONFIG.attachment.displayName,
    pluralName: 'Attachments',
    searchable: true,
  },
  protocol: {
    glyph: bowerGlyph('protocol'),
    ...NEUTRAL_TILE,
    displayName: ARTIFACT_TYPE_CONFIG.protocol.displayName,
    pluralName: 'Protocols',
    searchable: true,
  },
  equipment: {
    glyph: materialGlyph(ARTIFACT_TYPE_CONFIG.equipment.icon),
    ...NEUTRAL_TILE,
    displayName: ARTIFACT_TYPE_CONFIG.equipment.displayName,
    pluralName: 'Equipment',
    searchable: true,
  },
  // Without an entry here, `getEntityIconConfig` falls back to `note`, so a
  // spreadsheet would render with a note icon labelled "Note" across chips,
  // badges, tiles, the hierarchy tree and Bird's entity links. A Bower glyph,
  // not Material, so it takes the same gradient as its siblings.
  spreadsheet: {
    glyph: bowerGlyph('spreadsheet'),
    ...NEUTRAL_TILE,
    displayName: ARTIFACT_TYPE_CONFIG.spreadsheet.displayName,
    pluralName: 'Spreadsheets',
    searchable: true,
  },
  // A shortcut to an outside page (docs/planning/link-artifacts.md). Material,
  // like equipment: Bower has no link artwork yet, and GlyphRef carries the
  // library. Not `searchable`: search has no Link chip.
  link: {
    glyph: materialGlyph(ARTIFACT_TYPE_CONFIG.link.icon),
    ...NEUTRAL_TILE,
    displayName: ARTIFACT_TYPE_CONFIG.link.displayName,
    pluralName: 'Links',
  },
};

/**
 * Get icon configuration for an entity type. Tolerates `null`,
 * `undefined`, or unknown strings and falls back to the `note` config
 * so the call site can render *something* instead of crashing on
 * `config.displayName` etc. Matches the `?? note` shape used by the
 * other helpers in this file (getHierarchyNodeIcon).
 */
export const getEntityIconConfig = (
  entityType: EntityType | string | null | undefined,
): EntityIconConfig => {
  if (entityType && entityType in ENTITY_ICONS) {
    return ENTITY_ICONS[entityType as EntityType];
  }
  return ENTITY_ICONS.note;
};

/**
 * Get user-facing display name for an entity type (e.g. "Collection", "Note")
 */
export const getEntityDisplayName = (entityType: string): string => {
  return ENTITY_ICONS[entityType as EntityType]?.displayName ?? entityType;
};

/**
 * Get user-facing plural display name for an entity type (e.g. "Collections", "Notes")
 */
export const getEntityPluralName = (entityType: string): string => {
  return ENTITY_ICONS[entityType as EntityType]?.pluralName ?? `${entityType}s`;
};

/**
 * Resolve the glyph for a hierarchy node, including artifact subtypes.
 * Use this instead of hardcoding icon names in tree/list components.
 *
 * Artifacts in the database are a generic type; their visual icon depends on
 * the artifactType field (note, attachment, file).
 *
 * Returns a {@link GlyphRef}, not a Bower name. That is a fix, not a widening:
 * while this returned `BowerIconName` it read the registry's `icon` field and
 * ignored the `materialIcon` override, so `equipment` — the one entry drawn
 * from the other library — rendered its Bower glyph in trees and its Material
 * glyph everywhere else. One registry, two answers, which is the whole reason
 * `GlyphRef` exists.
 *
 * Artifact nodes resolve through `getFileGlyph`, the same resolver the card
 * (`FileTypeGlyph`) and the source pill use. The tree
 * agrees with them only once `HierarchyNode` carries `mimeType` /
 * `fileExtension`: today it does not, so every production caller passes no
 * `file` and an uploaded file draws the generic file page (never the
 * paperclip), while the card and the pill draw its kind. `file` is the seam
 * for that.
 */
export const getHierarchyNodeGlyph = (
  nodeType: string,
  artifactType?: string | null,
  options: {
    readonly file?: Pick<FileFacts, 'mimeType' | 'fileExtension'>;
  } = {},
): GlyphRef => {
  if (nodeType === 'artifact' && artifactType) {
    return fileGlyphRef(
      getFileGlyph({
        artifactType,
        mimeType: options.file?.mimeType ?? null,
        fileExtension: options.file?.fileExtension ?? null,
      }),
    );
  }

  const config = ENTITY_ICONS[nodeType as EntityType];
  return config?.glyph ?? ENTITY_ICONS.project.glyph;
};

/**
 * The folder entity's word, lowercase, for mid-sentence copy ("This
 * collection is empty"). Follows the display name, so it changes with the
 * deferred Collections to Folders rename.
 */
export const FOLDER_WORD = ENTITY_ICONS.project.displayName.toLowerCase();
